import React, { FC } from 'react';
import { Skeleton, Space, Tag, Typography, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Location, Terminal } from '~/types';
import { TextField, Card } from '~/components';
import { Longdash } from '~/utils';
import DeviceIcon from '~/components/DeviceIcon';
import UnknownAccount from '~/pages/accounts/components/UnknownAccount';

interface Props {
  data: Terminal;
  loading: boolean;
  locationsById: Record<string, Location> | undefined;
  hasAccessView: boolean;
  showOpen: (id: string) => void;

  accountMap?: Map<string, { id: string; number?: string }>;
  accountsDataLoading?: boolean;
}

const TerminalCard: FC<Props> = (props) => {
  const {
    data,
    loading,
    locationsById = {},
    hasAccessView,
    showOpen,
    accountMap,
    accountsDataLoading,
  } = props;
  const { t } = useTranslation('terminals');
  const accountIds = data?.accountIds || [];
  const firstId = accountIds[0];
  const restIds = accountIds.slice(1);
  const account = accountMap?.get(firstId);
  return (
    <StyledCard
      loading={loading}
      className="TerminalCard"
      status={data.status}
      onClick={() => hasAccessView && data.id && showOpen(data.id)}
    >
      <div className={`TerminalCard-Content ${!data.id && 'TerminalCard-Content__wait'}`}>
        <div className="TerminalCard-ImageWrapper">
          {data.additionalProperties?.iconURL ? (
            <img src={data.additionalProperties?.iconURL} alt="terminal" />
          ) : (
            <DeviceIcon
              osName={data?.device?.osName}
              active={data?.status === 'ACTIVE'}
              className="TerminalCard-ImagePlaceholder"
              type={data?.terminalType}
            />
          )}
        </div>
        <Space direction="vertical" className="TerminalCard-Space">
          <Typography.Title level={5} className="ellipsis">
            <Tooltip title={data.label} placement="top">
              <span>{data.label}</span>
            </Tooltip>
          </Typography.Title>

          <TextField label={t('type')} className="ellipsis">
            {data?.terminalType ? t(data?.terminalType) : Longdash}
          </TextField>

          {accountsDataLoading ? (
            <Skeleton.Button active size="small" style={{ height: 16, minWidth: 100 }} />
          ) : (
            <LinkedAccountsRow>
              <TextField label={t('linkedAccounts')} className="ellipsis">
                {account ? (
                  <Tooltip title={account.number} placement="top">
                    <span>{account.number}</span>
                  </Tooltip>
                ) : firstId ? (
                  <UnknownAccount />
                ) : (
                  Longdash
                )}
              </TextField>
              {restIds.length > 0 && <Tag>{`+${restIds.length}`}</Tag>}
            </LinkedAccountsRow>
          )}

          <TextField label={t('location')} className="ellipsis">
            {data?.locationId ? (
              <Tooltip title={locationsById[data.locationId]?.name} placement="top">
                <span>{locationsById[data.locationId]?.name}</span>
              </Tooltip>
            ) : (
              Longdash
            )}
          </TextField>
        </Space>
      </div>
    </StyledCard>
  );
};

const LinkedAccountsRow = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  .TextField {
    flex: 1;
    min-width: 0;
  }
  .ant-tag {
    flex-shrink: 0;
    margin-right: 0;
  }
`;

const StyledCard = styled(Card)`
  height: 180px;
  white-space: nowrap;
  cursor: pointer;
  .ant-space {
    display: flex;
    .ant-space-item {
      display: flex;
      max-width: 100%;
    }
  }
  .TerminalCard-Content {
    display: flex;
    .TerminalCard-ImageWrapper {
      width: 40%;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      img {
        max-height: 125px;
      }
      .TerminalCard-ImagePlaceholder {
        font-size: 104px;
      }
    }
    .TerminalCard-Space {
      margin-left: 24px;
      width: 60%;
    }
  }
  .TerminalCard-Content__wait {
    cursor: wait;
    opacity: 0.5;
  }
  .ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export default TerminalCard;
